import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Card, Drawer, Empty, Select, Space, Typography } from 'antd';
import { space } from '@viq-admin/ui';
import { CaptureCard } from './CaptureCard';
import { useLiveFeed } from './useLiveFeed';
import type { LiveFeedConference, LiveFeedFetcher } from './types';

export interface LiveFeedScreenProps {
  /** Conferences to choose from — already scoped by the shell (all tenants for
   *  the console; the caller's company for the customer portal). */
  conferences: LiveFeedConference[];
  /** How to fetch a feed page for the selected conference (shell-provided). */
  fetchLiveFeed: LiveFeedFetcher;
  /** Renders the lead-detail body inside the drawer (each shell's own). */
  renderLeadDetail: (leadId: string) => ReactNode;
  conferencesLoading?: boolean;
}

// Grace window after a conference's last day before it drops off the picker.
const LIVE_FEED_GRACE_MS = 24 * 60 * 60 * 1000; // 1 day

function isCurrentConference(endDate?: string | null): boolean {
  if (!endDate) return true; // no end date → always eligible
  const end = new Date(endDate).getTime();
  if (Number.isNaN(end)) return true; // unparseable → don't hide
  // endDate is the last DAY (00:00); the conference runs through that day, so
  // treat it as ending at the next midnight, then allow the grace window.
  const endOfDay = end + 24 * 60 * 60 * 1000;
  return Date.now() <= endOfDay + LIVE_FEED_GRACE_MS;
}

/**
 * Shared Live-feed screen: pick a conference → poll captures newest-first every
 * ~7s → click a capture for lead detail. Parameterized per ADR 016; no plane- or
 * router-specific logic. Uses antd theme tokens so light/dark is automatic.
 * Long-past conferences (ended more than ~1 day ago) are hidden from the picker.
 */
export function LiveFeedScreen({
  conferences,
  fetchLiveFeed,
  renderLeadDetail,
  conferencesLoading,
}: LiveFeedScreenProps) {
  const [conferenceId, setConferenceId] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  // Drop long-past conferences (ended > ~1 day ago) from the picker.
  const visibleConferences = useMemo(
    () => conferences.filter((c) => isCurrentConference(c.endDate)),
    [conferences],
  );

  // Default to the first available conference once the list loads.
  useEffect(() => {
    if (!conferenceId && visibleConferences.length) {
      setConferenceId(visibleConferences[0].id);
    }
  }, [conferenceId, visibleConferences]);

  const { state, acknowledgeNew, setScrollLocked } = useLiveFeed(
    conferenceId,
    fetchLiveFeed,
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollLocked(el.scrollTop > 24);
  };

  const conferenceOptions = useMemo(
    () =>
      visibleConferences.map((c) => ({
        label: `${c.name}${c.location ? ` — ${c.location}` : ''}`,
        value: c.id,
      })),
    [visibleConferences],
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space wrap>
          <Typography.Text>Conference:</Typography.Text>
          <Select
            style={{ width: 320, maxWidth: '100%' }}
            value={conferenceId ?? undefined}
            options={conferenceOptions}
            loading={conferencesLoading}
            placeholder="Pick a conference"
            showSearch
            optionFilterProp="label"
            onChange={(v) => setConferenceId(v)}
          />
          {state.reconnecting && (
            <Alert type="warning" message="Reconnecting…" showIcon banner />
          )}
        </Space>
      </Card>
      <Card
        size="small"
        title={
          <Space>
            <span>Captures</span>
            {state.newCount > 0 && state.scrollLocked && (
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                  acknowledgeNew();
                }}
              >
                {state.newCount} new
              </Button>
            )}
          </Space>
        }
      >
        <div
          ref={scrollRef}
          onScroll={onScroll}
          style={{ maxHeight: 600, overflowY: 'auto', paddingRight: space.sm }}
        >
          {state.items.length === 0 ? (
            <Empty
              description={
                conferenceId
                  ? 'No captures yet — they appear as field reps record them.'
                  : 'Pick a conference above to start.'
              }
            />
          ) : (
            state.items.map((c) => (
              <CaptureCard key={c.id} capture={c} onOpen={setSelectedLeadId} />
            ))
          )}
        </div>
      </Card>
      <Drawer
        title="Lead detail"
        width={820}
        open={!!selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
        destroyOnClose
      >
        {selectedLeadId && renderLeadDetail(selectedLeadId)}
      </Drawer>
    </Space>
  );
}
